<script setup lang="ts">
import {computed, ref, type Component} from "vue";
import {ChevronDown, Search, X} from "@lucide/vue";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuTrigger
} from "@/core/components/ui/dropdown-menu";
import {ProbeDraft, ProbeLine, ProbeView} from "@/medication-checkup/interfaces/probe";
import {CARD_SHEET, FLAGGED, pillClasses} from "@/medication-checkup/services/SceneClasses";
import MedicationCheckupYesNo from "@/medication-checkup/components/MedicationCheckupYesNo.vue";
import MedicationCheckupVidalSearch from "@/medication-checkup/components/MedicationCheckupVidalSearch.vue";
import {probeLabel} from "@/medication-checkup/composables/useProbeScene";
import {VidalEntry} from "@/medication-checkup/interfaces/patient-profile";

/**
 * The card of a probe: one yes/no question and, on a « yes », the effects
 * observed — picked among the ones the probe proposes, found in Vidal where
 * the probe searches it, or written in the pharmacist's words — over the
 * treatments suspected of carrying them.
 *
 * Answering is local to the scene: the card shows the drafts it is handed
 * while live, the findings the server froze once the block is completed.
 * The icon is the scene's: the handler serves words, not presentation.
 */

const props = defineProps<{
    view: ProbeView;
    /**
     * The icon drawn beside the title.
     */
    icon: Component;
    frozen?: boolean;
    /**
     * The answer given so far, while the probe is live.
     */
    answer?: boolean | null;
    /**
     * The findings drafted so far, while the probe is live.
     */
    drafts?: ProbeDraft[];
    /**
     * Whether an effect is searched in Vidal beside the ones proposed, or
     * added as typed when Vidal does not name it.
     */
    searchable?: boolean;
    /**
     * What the scene points at as missing: `answer`, the question, and
     * `other`, « Autre » left blank.
     */
    flagged?: string[];
}>();

const emit = defineEmits<{
    answer: [given: boolean];
    'update:drafts': [drafts: ProbeDraft[]];
}>();

/**
 * What the probe reads as answered: the local answer while live, the one the
 * server froze once the block is completed.
 */
const answered = computed(() => props.frozen ? props.view.answer : props.answer ?? null);

const drafts = computed(() => props.drafts ?? []);

/**
 * The effects the pharmacist may pick: the ones the probe proposes, the ones
 * a search added, then « Autre » for what the catalogue does not name, where
 * the probe offers it.
 */
const options = computed(() => [
    ...props.view.options,
    ...drafts.value
        .map(draft => draft.option)
        .filter(option => !props.view.options.includes(option) && option !== props.view.other),
    ...props.view.other ? [props.view.other] : [],
]);

const draftOf = (option: string): ProbeDraft | undefined => {
    return drafts.value.find(draft => draft.option === option);
};

/**
 * The draft of « Autre », whose detail the pharmacist types.
 */
const otherDraft = computed(() => props.view.other ? draftOf(props.view.other) : undefined);

/**
 * What a draft reads as, once submitted.
 */
const labelOf = (draft: ProbeDraft): string => probeLabel(draft, props.view.other);

const searching = ref(false);

/**
 * Add an effect found in Vidal or typed, unless it is already drafted.
 */
const add = (option: string) => {
    if (option !== '' && !draftOf(option)) {
        emit('update:drafts', [...drafts.value, {option, detail: '', line_ids: []}]);
    }

    searching.value = false;
};

const flaggedClasses = (key: string): string => props.flagged?.includes(key) ? FLAGGED : '';

const linesOf = (lineIds: number[]): ProbeLine[] => {
    return props.view.lines.filter(line => lineIds.includes(line.id));
};

/**
 * What the trigger of the suspected treatments reads: their names once some
 * are picked, the invitation to pick otherwise.
 */
const suspectedLabel = (draft: ProbeDraft): string => {
    const names = linesOf(draft.line_ids).map(line => line.label.split(' ')[0]);

    return names.length > 0 ? names.join(', ') : 'sélectionnez les produits en cause';
};

/**
 * Pick an effect, or drop it if already picked.
 */
const toggleOption = (option: string) => {
    const draft = draftOf(option);

    emit('update:drafts', draft
        ? drafts.value.filter(current => current !== draft)
        : [...drafts.value, {option, detail: '', line_ids: []}]);
};

const remove = (draft: ProbeDraft) => {
    emit('update:drafts', drafts.value.filter(current => current !== draft));
};

const replace = (draft: ProbeDraft, next: ProbeDraft) => {
    emit('update:drafts', drafts.value.map(current => current === draft ? next : current));
};

const toggleLine = (draft: ProbeDraft, line: ProbeLine) => {
    replace(draft, {
        ...draft,
        line_ids: draft.line_ids.includes(line.id)
            ? draft.line_ids.filter(id => id !== line.id)
            : [...draft.line_ids, line.id]
    });
};

const detail = (draft: ProbeDraft, event: Event) => {
    replace(draft, {...draft, detail: (event.target as HTMLInputElement).value});
};
</script>

<template>
  <div
    :class="CARD_SHEET"
    class="relative z-10 max-w-[640px] rounded-xl"
  >
    <div class="flex items-center gap-2 px-4 py-3">
      <component :is="icon" class="w-[18px] h-[18px] text-primary" />
      <span class="text-sm font-semibold text-primary">{{ view.title }}</span>
    </div>

    <div
      class="flex flex-col gap-2.5 mx-2 px-2 py-1.5 mb-2"
      :class="flaggedClasses('answer')"
      data-question-ref="answer"
    >
      <span class="text-sm leading-snug">{{ view.question }}</span>

      <MedicationCheckupYesNo
        :answer="answered"
        :disabled="frozen"
        @answer="emit('answer', $event)"
      />
    </div>

    <div
      v-if="!frozen && answered === true"
      class="flex flex-col gap-2.5 px-4 py-3 border-t border-gray-light animate-message-in"
    >
      <span class="text-xs text-muted-foreground">1. Sélectionnez l'effet constaté, puis les produits en cause :</span>

      <div class="flex flex-wrap gap-1.5">
        <span
          v-for="option in options"
          :key="option"
          class="inline-flex items-stretch"
        >
          <button
            class="px-3 py-1 border-[1.5px] border-dashed text-xs font-medium"
            :class="[pillClasses(!!draftOf(option)), draftOf(option) && view.lines.length > 0 ? 'rounded-l-full' : 'rounded-full']"
            @click="toggleOption(option)"
          >
            {{ option }}
          </button>

          <DropdownMenu v-if="draftOf(option) && view.lines.length > 0">
            <DropdownMenuTrigger as-child>
              <button
                class="inline-flex items-center gap-1 pl-2.5 pr-2 max-w-[280px] rounded-r-full border-[1.5px] border-l-0 border-solid border-gray bg-gray-light text-xs font-semibold truncate"
                :class="draftOf(option)!.line_ids.length > 0 ? 'text-primary' : 'text-gray-dark'"
              >
                <span class="truncate">{{ suspectedLabel(draftOf(option)!) }}</span>
                <ChevronDown class="w-3.5 h-3.5 shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" class="min-w-[250px]">
              <DropdownMenuLabel class="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                Produits en cause — sélection multiple
              </DropdownMenuLabel>
              <DropdownMenuCheckboxItem
                v-for="line in view.lines"
                :key="line.id"
                class="cursor-pointer"
                :model-value="draftOf(option)!.line_ids.includes(line.id)"
                @update:model-value="toggleLine(draftOf(option)!, line)"
                @select.prevent
              >
                {{ line.label }}
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </span>

        <button
          v-if="searchable && !searching"
          class="inline-flex items-center gap-1 px-3 py-1 rounded-full border-[1.5px] border-dashed border-gray bg-white text-gray-dark text-xs font-medium hover:border-primary hover:text-primary"
          @click="searching = true"
        >
          <Search class="w-3 h-3" />
          Rechercher un autre effet
        </button>
      </div>

      <MedicationCheckupVidalSearch
        v-if="searchable && searching"
        kind="side_effect"
        free
        :picked="(entry: VidalEntry) => !!draftOf(entry.title)"
        @pick="entry => add(entry.title)"
        @add="add"
        @dismiss="searching = false"
      />

      <input
        v-if="otherDraft"
        :value="otherDraft.detail"
        placeholder="Précisez l'effet…"
        class="self-start w-[260px] px-2.5 py-1.5 rounded-lg border border-solid border-slate-200 bg-white text-sm outline-none focus:border-primary"
        :class="flaggedClasses('other')"
        data-question-ref="other"
        @input="detail(otherDraft, $event)"
      >

      <ul v-if="drafts.length > 0" class="flex flex-col gap-1">
        <li
          v-for="draft in drafts"
          :key="draft.option"
          class="flex items-center gap-2 text-sm"
        >
          <span class="font-medium">{{ labelOf(draft) }}</span>
          <span class="text-xs text-muted-foreground">
            {{ linesOf(draft.line_ids).map(line => line.label).join(', ') || 'Traitement non précisé' }}
          </span>
          <button
            class="ml-auto p-0.5 rounded text-muted-foreground hover:text-red"
            @click="remove(draft)"
          >
            <X class="w-3.5 h-3.5" />
          </button>
        </li>
      </ul>
    </div>

    <ul
      v-if="frozen && view.findings.length > 0"
      class="flex flex-col gap-1 px-4 py-3 border-t border-gray-light"
    >
      <li
        v-for="finding in view.findings"
        :key="finding.label"
        class="flex items-center gap-2 text-sm"
      >
        <span class="font-medium">{{ finding.label }}</span>
        <span class="text-xs text-muted-foreground">
          {{ finding.lines.map(line => line.label).join(', ') || 'Traitement non précisé' }}
        </span>
      </li>
    </ul>
  </div>
</template>
